/* tslint:disable:no-trailing-whitespace */
import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { DatePipe } from '@angular/common';
import { get, isEmpty, some } from 'lodash';
import { ArrayUtils } from '../../utils/Array.utils';
import { InterpolateService } from '../../services/interpolate.service';
import { IWidgetItem } from '@ispm/widgets/interfaces/widget-item.interface';
import {
  IdynamicFormItem,
  IDynamicFormItemIcon,
  IDynamicFormItemTooltipPattern,
  IDynamicFormItemTooltipWidget
} from '@ispm/dynamic-form/interfaces/IdynamicFormItem';
import { Widget } from '@ispm/widgets/decorator/widget.decorator';
import { InterpolationOptions } from '@ispm/shared/services/interpolate.service';
import { TranslateService } from '@ngx-translate/core';
import {RichTextUtils} from '@ispm/shared/classes/RichTextUtils';

@Component({
  selector: 'gen-textual-content',
  templateUrl: './textual-content.component.html',
  styleUrls: ['./textual-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
@Widget('TextualContent')
export class TextualContentComponent implements OnInit, IWidgetItem, AfterViewInit {
  @Input()
  set fieldConfig(fieldConfig: IResourceField) {
    this._fieldConfig = fieldConfig;
    this.setValue();
  }

  get fieldConfig() {
    return this._fieldConfig;
  }

  @Input()
  set item(item: any) {
    this._item = item;
    this.setValue();
  }

  get item() {
    return this._item;
  }

  @Input() isItDefault: boolean;

  @Input()
  set tooltipText(value: string) {
    if (value) {
      this._tooltipText = value;
    }
  }

  get tooltipText() {
    return this._tooltipText;
  }

  @Input()
  set tooltipConfiguration(configuration: IDynamicFormItemTooltipPattern | IDynamicFormItemTooltipWidget) {
    if (configuration) {
      this._tooltipConfiguration = configuration;
    }
  }

  get tooltipConfiguration() {
    return this._tooltipConfiguration;
  }

  @Input()
  set tooltipPosition(value: string) {
    if (value) {
      this._tooltipPosition = value;
    }
  }

  get tooltipPosition() {
    return this._tooltipPosition;
  }

  set widgetConfig(widgetConfig: IdynamicFormItem<IResourceField>) {
    this.fieldConfig = get(widgetConfig, 'widgetPayload', { });
  }

  set widgetData(widgetData: any) {
    this.item = widgetData;
  }

  constructor(
    private _interpolatorService: InterpolateService,
    private datePipe: DatePipe,
    private _translateService: TranslateService
  ) {
  }

  INTERPOLATION_DEFAULT_OPTIONS: InterpolationOptions = {
    useEmptyValues: false,
    usePartialPath: false,
    clearUnresolvedPatterns: true
  };

  private _fieldConfig: IResourceField;

  private _item: any;
  private _fieldAttributes: any;

  @Input()
  fieldType: string;

  @Input() set fieldAttributes(fieldAttributes: any) {
    this._fieldAttributes = fieldAttributes;
    if (this._fieldAttributes) {
      this.setValue();
    }
  }

  private _tooltipText: string;

  private _tooltipConfiguration: IDynamicFormItemTooltipPattern | IDynamicFormItemTooltipWidget;

  private _tooltipPosition: string = 'BOTTOM';

  public hyphenate: boolean = false;

  public reapply: boolean = false;

  @Input()
  public icon: IDynamicFormItemIcon;

  @Input()
  public showTitle: boolean = false;

  @Input()
  public checkBlank: boolean = false;

  public values: Array<any>;

  @Output()
  linkClick: EventEmitter<string> = new EventEmitter<string>();

  @Input()
  tooltipData: any;

  ngAfterViewInit(): void {
    this.reapply = false;
  }

  ngOnInit() {
    if (this.showTitle) {
      this.showTitle = !this.shouldHideTitle();
    }
  }

  shouldHideTitle() {
    const nonEmptyVal: boolean = some(this.values, v => !this.valueIsEmpty(v));
    return this.fieldConfig.hideIfEmpty && !nonEmptyVal;
  }

  valueIsEmpty(v: any): boolean {
    return !(v && v.value);
  }

  escapeChevron(val: string): string {
    return RichTextUtils.escapeChevron(val);
  }

  setValue() {
    this.values = [];

    if ((!this.item && this.item !== 0) || !this.fieldConfig) {
      return;
    }
    const isLink = (this.fieldConfig.link || this.fieldConfig.linkPath);
    if (!this.fieldConfig.type) {
      this.fieldConfig.type = isLink
        ? 'link'
        : 'text';
    }
    let value = this.getItemFieldValue(this.item, this.fieldConfig);
    this.checkIfShouldHyphenate();
    if (this.fieldConfig.pattern) {
      value = Array.isArray(value) ? value : [value];
      const requireInterpolation = InterpolateService.hasInterpolationString(this.fieldConfig.pattern);

      value = value.map(val => {
        let interpolated: string;

        if (requireInterpolation) {
          interpolated = this._interpolatorService.interpolate(this.fieldConfig.pattern, val, false, this.setInterpolationOptions());
          const pattern = this.fieldConfig.pattern.replace(new RegExp(InterpolateService.INTERPOLATION_REGEX, 'g'), '');
          if (interpolated && interpolated === pattern) {
            return this.fieldConfig.emptyValue || '';
          }
        } else {
          interpolated = this.fieldConfig.pattern;
        }

        return interpolated.replace(/\(\)/gi, '');
      }).join(this.fieldConfig.separator || ' ');
    } else if (this.fieldConfig.type !== 'link' && Array.isArray(value)) {
        if (this.fieldConfig.removeDuplications) {
          value = [].concat.apply([], value);
          value = new Set(value);
          value = Array.from(value);
        }
        value = value.filter(val => val !== null).join(this.fieldConfig.separator || ', ');
    }

    if (value instanceof Date || get(this._fieldAttributes, 'transformToDate', false)) {
      const datePipe = new DatePipe('en-US');
      try {
        value = datePipe.transform(value, get(this._fieldAttributes, 'dateFormat', 'dd MMM y'));
      } catch (e) {
        console.log('Invalid Date', e);
      }
    }
    if (this.fieldConfig.type === 'link') {
      this.setValueWithLink(value);
    } else {
      this.values.push({ value});
    }

    if (this.showTitle) {
      this.showTitle = !this.shouldHideTitle();
    }
    this.reapply = true;
  }

  setValueWithLink(values: Array<string>) {
    let links = this.fieldConfig.link || this.getItemFieldValue(this.item, this.fieldConfig, 'linkPath');
    if (!Array.isArray(links)) {
      links = [links];
    }
    if (!Array.isArray(values)) {
      values = [values];
    }
    for (let i = 0; i < links.length; i++) {
      this.values.push({ value: values[i], link: links[i]});
    }
  }

  getItemFieldValue(item, field: IResourceField, resourcePath: 'path' | 'linkPath' = 'path') {
    let paths = [field[resourcePath]];
    if (field[resourcePath]) {
      paths = ArrayUtils.getArrayRelatedPath(field[resourcePath]);
    }
    return this.getFieldRecursively(item, paths, 0);
  }

  getFieldRecursively(item, paths, index) {
    if (!(paths.length > index && !!paths[index])) {
      return item;
    }
    if (Array.isArray(item)) {
      return item.length === 0 ? null : item.map(rec => this.getFieldRecursively(rec, paths, index));
    } else if (item && typeof item === 'object') {
      return this.getFieldRecursively(get(item, paths[index], null), paths, index + 1);
    } else {
      return item;
    }
  }

  linkClicked(fieldName: string) {
    if (this.fieldConfig.parentHandleLink) { // link click should be handled by parent component
      this.linkClick.emit(fieldName);
      return false; // prevent opening the link by the current component
    }
  }

  public needShowValue(value: any): boolean {
    if (this.checkBlank) {
      return value != undefined
        && !(value !== 0 && isEmpty(value)); // check if blank value and not the int 0
    } else {
      return value != undefined;
    }
  }

  setInterpolationOptions() {
    if (this.fieldConfig.interpolationOptions) {
      return Object.assign({ }, this.INTERPOLATION_DEFAULT_OPTIONS, this.fieldConfig.interpolationOptions);
    }

    return this.INTERPOLATION_DEFAULT_OPTIONS;
  }

  checkIfShouldHyphenate(): void {
    this.hyphenate = get(this.fieldConfig, 'hyphenate', false);
  }

  trackByMethod(index: number, item: any) {
    return item.value
      ? `${item.value}_${index}`
      : index;
  }

  trimwhiteSpace(value: any) {
    if (typeof value === 'string' || value instanceof String) {
     return value.trim();
    }
    return value;
    }
}

export interface IResourceField {
  label?: string;
  path?: string;
  type?: 'text' | 'link' | string;
  separator?: string;
  emptyValue?: string;
  hideIfEmpty?: boolean;
  hyphenate?: boolean;
  pattern?: string;
  link?: string;
  linkPath?: string;
  cssClasses?: string;
  parentHandleLink?: boolean;
  showLinkSuffix?: boolean;
  interpolationOptions?: InterpolationOptions;
  itemCssClass?: string;
  removeDuplications?: boolean;
}
